"use client"

import { useState, useMemo } from "react"
import { UseFormReturn, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { PlusCircle, ListTodo } from "lucide-react"
import { Button } from "@/components/ui/button"
import { FormSectionCard } from "./FormSectionCard"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Switch } from "@/components/ui/switch"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { useLanguage } from "../../contexts/language-context"
import { useFormSectionNavigation } from "@/hooks/use-form-section-navigation"
import { type TaskFormData as Task, type TaskFormArrayData, taskFormSchema } from "@/lib/schemas"
import { TasksTable } from "./TasksTable"
import { ENGAGEMENT_TYPE, ENGAGEMENT_BILLING_TYPE } from "@/lib/constants/engagement-codes"
import React from "react"

interface TasksSectionProps {
  form: UseFormReturn<TaskFormArrayData>
  onTaskAdd: (task: Task) => void
  onTaskEdit: (task: Task) => void
  onTaskRemove: (id: number) => void
  isCompleted: boolean
  isReadonly?: boolean
  isChangeRequest?: boolean
  engagementType?: string
  billingType?: string
  hiddenSections: Record<string, boolean>
}

export const TasksSection = ({ 
  form, 
  onTaskAdd, 
  onTaskEdit,
  onTaskRemove,
  isCompleted,
  isReadonly = false,
  isChangeRequest = false,
  engagementType,
  billingType,
  hiddenSections,
}: TasksSectionProps) => {
  const { dictionary } = useLanguage()
  const navigationProps = useFormSectionNavigation('tasks', hiddenSections)
  
  const tasks = form.watch('tasks')
  
  // Check if this is an internal engagement
  const isInternalEngagement = engagementType === ENGAGEMENT_TYPE.INTERNAL
  
  // Check if billing type is milestone
  const isMilestoneBilling = billingType === ENGAGEMENT_BILLING_TYPE.MILESTONE
  
  // Billable tasks are disallowed for internal engagements or milestone billing
  const isBillableDisallowed = isInternalEngagement || isMilestoneBilling
  
  // Default task template - conditional based on engagement type and billing type
  const getDefaultTask = (): Partial<Task> => ({
    name: "",
    billable: !isBillableDisallowed, // Non-billable for internal engagements or milestone billing
    inactive: false,
    isUtilized: true,
  })
  
  // Local state for task dialog (handles both add and edit)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  
  // Memoize the task form resolver to prevent unnecessary recreation
  const taskFormResolver = useMemo(() => {
    return zodResolver(taskFormSchema)
  }, [])

  // Form for dialog task with validation
  const dialogForm = useForm<Task>({
    resolver: taskFormResolver,
    mode: 'onChange',
    defaultValues: getDefaultTask()
  })

  const openAddDialog = () => {
    // Generate incremental ID based on existing tasks
    const currentTasks = form.getValues('tasks')
    const maxId = currentTasks.length > 0 ? Math.max(...currentTasks.map(t => t.id)) : 0
    const newTask = {
      ...getDefaultTask(),
      id: maxId + 1 // Assign incremental ID
    }
    dialogForm.reset(newTask)
    dialogForm.trigger()
    setIsEditMode(false)
    setIsDialogOpen(true)
  }

  const openEditDialog = (task: Task) => {
    dialogForm.reset(task)
    dialogForm.trigger()
    setIsEditMode(true)
    setIsDialogOpen(true)
  }

  const closeDialog = () => {
    setIsDialogOpen(false)
    dialogForm.reset(getDefaultTask() as Task)
    setIsEditMode(false)
  }

  const handleSaveTask = dialogForm.handleSubmit((data) => {
    if (isEditMode) {
      // Edit existing task
      onTaskEdit(data)
    } else {
      // Add new task
      onTaskAdd(data)
    }
    closeDialog()
  })

  const removeTask = (id: number) => {
    onTaskRemove(id)
  }

  const toggleTaskStatus = (task: Task) => {
    onTaskEdit({ ...task, inactive: !task.inactive })
  }

  return (
    <FormSectionCard
      id="tasks"
      title={dictionary?.engagement?.tasks?.title || "Tasks"}
      description={dictionary?.engagement?.tasks?.description || "Manage tasks associated with the engagement"}
      icon={ListTodo}
      isCompleted={isCompleted}
      showPrevNext={true}
      {...navigationProps}
    >
      <div className="flex justify-end">
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1" disabled={isReadonly} onClick={openAddDialog}>
              <PlusCircle className="h-4 w-4" />
              {dictionary?.engagement?.tasks?.addTask || "Add Task"}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                {isEditMode 
                  ? (dictionary?.engagement?.tasks?.editTask || "Edit Task")
                  : (dictionary?.engagement?.tasks?.addTask || "Add Task")
                }
              </DialogTitle>
              <DialogDescription>
                {isEditMode 
                  ? (dictionary?.engagement?.tasks?.editTaskDescription || "Update the task details")
                  : (dictionary?.engagement?.tasks?.addTaskDescription || "Add a new task to the engagement")
                }
              </DialogDescription>
            </DialogHeader>
            <Form {...dialogForm}>
              <div className="space-y-4 py-4">
                <FormField
                  control={dialogForm.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem className="space-y-2.5">
                      <FormLabel className="text-sm font-medium">
                        {dictionary?.engagement?.tasks?.taskName || "Task Name"}
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder={dictionary?.engagement?.tasks?.enterTaskName || "Enter task name"}
                          {...field}
                          className="form-field-focus"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {!isMilestoneBilling && (
                  <FormField
                    control={dialogForm.control}
                    name="billable"
                    render={({ field }) => (
                      <FormItem className="space-y-2.5">
                        <FormLabel className="text-sm font-medium">
                          {dictionary?.engagement?.tasks?.billable || "Billable"}
                        </FormLabel>
                        <FormControl>
                          <RadioGroup
                            value={field.value ? "billable" : "non-billable"}
                            onValueChange={(value) => field.onChange(value === "billable")}
                            className="flex gap-6"
                          >
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="billable" id="billable" disabled={isBillableDisallowed} />
                              <Label htmlFor="billable" className={isBillableDisallowed ? "text-muted-foreground" : ""}>
                                {dictionary?.engagement?.tasks?.billable || "Billable"}
                              </Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <RadioGroupItem value="non-billable" id="non-billable" />
                              <Label htmlFor="non-billable">
                                {dictionary?.engagement?.tasks?.nonBillable || "Non-Billable"}
                              </Label>
                            </div>
                          </RadioGroup>
                        </FormControl>
                        {isBillableDisallowed && (
                          <p className="text-xs text-muted-foreground">
                            {isInternalEngagement
                              ? (dictionary?.engagement?.tasks?.internalNonBillableNote || "Internal engagements can only have non-billable tasks")
                              : (dictionary?.engagement?.tasks?.milestoneNonBillableNote || "Milestone billing engagements can only have non-billable tasks")
                            }
                          </p>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {isMilestoneBilling && (
                  <FormField
                    control={dialogForm.control}
                    name="isUtilized"
                    render={({ field }) => (
                      <FormItem className="flex items-center justify-between space-y-0 rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <FormLabel className="text-sm font-medium">
                            {dictionary?.engagement?.tasks?.isUtilized || "Is Utilized"}
                          </FormLabel>
                          <p className="text-xs text-muted-foreground">
                            {dictionary?.engagement?.tasks?.isUtilizedDescription || "Track whether this task counts toward worker utilization"}
                          </p>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value !== false}
                            onCheckedChange={field.onChange}
                            disabled={isReadonly}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </Form>
            <DialogFooter>
              <Button variant="outline" onClick={closeDialog}>
                {dictionary?.engagement?.core?.cancel || "Cancel"}
              </Button>
              <Button onClick={handleSaveTask}>
                {isEditMode 
                  ? (dictionary?.engagement?.tasks?.updateTask || "Update Task")
                  : (dictionary?.engagement?.tasks?.addTask || "Add Task")
                }
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Show form-level errors when tasks exist but validation fails */}
      {tasks.length > 0 && form.formState.errors.tasks && typeof form.formState.errors.tasks === 'object' && 'message' in form.formState.errors.tasks && (
        <div className="border border-destructive/20 rounded-md p-4 bg-destructive/5">
          <div className="flex items-center gap-2 text-destructive">
            <span className="text-sm font-medium">
              {form.formState.errors.tasks.message}
            </span>
          </div>
        </div>
      )}

      <TasksTable
        tasks={tasks}
        onEditTask={openEditDialog}
        onRemoveTask={removeTask}
        onTaskToggleStatus={toggleTaskStatus}
        isReadonly={isReadonly}
        isChangeRequest={isChangeRequest}
        isMilestoneBilling={isMilestoneBilling}
        formErrors={form.formState.errors}
      />
    </FormSectionCard>
  )
}

export default TasksSection
